import { StoryCardPollContentOption, StoryCardType } from '../../../../../services';
import { GalleryItem } from '../../../../models';
import { isEmptyString } from '../../../../utils';
import { LinkPostArticleFields, LinkPostQuoteFields, PostType } from '../CreateCardModal';

interface ShortPostValidationArgs {
  isCategoryCard?: boolean;
  shortPostGallery: GalleryItem[];
  shortPostText: string;
}

interface LinkPostValidationArgs {
  linkPostType?: StoryCardType;
  linkPostArticle?: LinkPostArticleFields;
  linkPostQuote?: LinkPostQuoteFields;
}

interface PollPostValidationArgs {
  pollPostHeadline: string;
  pollPostOptions: StoryCardPollContentOption[];
}

interface GetPostCardValidationErrorArgs {
  postType: PostType;
  isCategoryCard?: boolean;
  shortPostGallery: GalleryItem[];
  shortPostText: string;
  fullPostTitle: string;
  threadPostTitle: string;
  linkPostType?: StoryCardType;
  linkPostArticle?: LinkPostArticleFields;
  linkPostQuote?: LinkPostQuoteFields;
  pollPostHeadline: string;
  pollPostOptions: StoryCardPollContentOption[];
  eventId: number | null;
}

const isShortPostInvalid = ({
  isCategoryCard,
  shortPostGallery,
  shortPostText,
}: ShortPostValidationArgs): boolean => {
  if (isCategoryCard) {
    return !shortPostGallery.length;
  }
  return !shortPostGallery.length && isEmptyString(shortPostText);
};

const isLinkPostInvalid = ({
  linkPostType,
  linkPostArticle,
  linkPostQuote,
}: LinkPostValidationArgs): boolean => {
  if (!linkPostType) {
    return true;
  }

  const { sourceName, title } = { ...linkPostArticle };
  const { quote, gallery } = { ...linkPostQuote };

  if (linkPostArticle && (isEmptyString(sourceName) || isEmptyString(title))) {
    return true;
  }

  if (linkPostQuote && isEmptyString(quote) && !gallery?.length) {
    return true;
  }

  return false;
};

const isPollPostInvalid = ({
  pollPostHeadline,
  pollPostOptions,
}: PollPostValidationArgs): boolean => {
  const optionTexts = pollPostOptions.map(({ text }) => text.trim().toLowerCase());
  const hasDuplicateOptions = new Set(optionTexts).size !== optionTexts.length;

  return (
    isEmptyString(pollPostHeadline) ||
    pollPostOptions.some(({ text }) => isEmptyString(text)) ||
    hasDuplicateOptions
  );
};

const isIgdbPostInvalid = (linkPostArticle?: LinkPostArticleFields): boolean => {
  const { sourceName, title } = { ...linkPostArticle };
  return !linkPostArticle || isEmptyString(sourceName) || isEmptyString(title);
};

export const getPostCardValidationError = ({
  postType,
  isCategoryCard,
  shortPostGallery,
  shortPostText,
  fullPostTitle,
  threadPostTitle,
  linkPostType,
  linkPostArticle,
  linkPostQuote,
  pollPostHeadline,
  pollPostOptions,
  eventId,
}: GetPostCardValidationErrorArgs): boolean => {
  switch (postType) {
    case PostType.SHORT:
      return isShortPostInvalid({ isCategoryCard, shortPostGallery, shortPostText });
    case PostType.FULL:
      return isEmptyString(fullPostTitle);
    case PostType.THREAD:
      return isEmptyString(threadPostTitle);
    case PostType.LINK:
      return isLinkPostInvalid({ linkPostType, linkPostArticle, linkPostQuote });
    case PostType.POLL:
      return isPollPostInvalid({ pollPostHeadline, pollPostOptions });
    case PostType.EVENT:
      return !eventId;
    case PostType.IGDB:
      return isIgdbPostInvalid(linkPostArticle);
    default:
      return false;
  }
};
